"use strict";

import {EventEmitter} from "events";
import hostProvider from "./../host";
import tokenProvider from "./../token";

const DEFAULT_PORT = 8000;
const DEFAULT_TIMEOUT = 10000;

export default class Connection extends EventEmitter {
	
	constructor() {
		
		super();
		
		this.retryTimer = null;
		this.ws = null;
		this.authenticated = false;
		
		this.requestQueue = [];
	}
	
	connect() {
		return new Promise((resolve, reject) => {
			
			if (this.authenticated)
				return resolve(this);
			
			hostProvider.get(fullHost => {
				const host = fullHost.split(":")[0];
				this.ws = new WebSocket("ws://"+host+':' + DEFAULT_PORT);	
				this.ws.onopen = this.openHandler.bind(this);
				this.ws.onclose = this.closeHandler.bind(this);
				this.ws.onerror = this.errorHandler.bind(this);
				this.ws.onmessage = this.messageHandler.bind(this);
				
				this.once("auth", _ => resolve(this));
				this.once("error", error => reject(error));
			});
		});
	}
	
	close() {
		this.removeAllListeners();
		this.ws.close();
		this.closeHandler();
	}

	push(request, params, callback) {
		
		this.requestQueue.push({
			request: request,
			params: params,
			callback: callback,
			state: 0
		});
	}

	iterateQueue() {
		
		var current = this.getFirstFromQueue();
		
		if (!current || current.state == 1)
			return;
		
		if (current.state == 2) {
			this.requestQueue.shift();
			this.iterateQueue();
			return;
		}
		
		current.state = 1;
		
		setTimeout(function(){
			if (current.state > 1) return;
			current.state = 2;
			current.callback({
				id: "TIMEOUT",
				message: "TIMEOUT"
			});
		}.bind(this), DEFAULT_TIMEOUT);
		
		this.send({
			request: current.request,
			params: current.params
		});
	}

	send(message) {
		
		if (this.ws.readyState !== 1)
			return;
		
		this.ws.send(JSON.stringify(message));
	}

	getFirstFromQueue() {
		return this.requestQueue.length > 0 ? this.requestQueue[0] : null;
	}

	openHandler() {
		if (this.retryTimer)
			clearTimeout(this.retryTimer);
		
		this.auth();
		//this.iterateQueue();
	}

	messageHandler(evt) {
		var message = JSON.parse(evt.data);
		
		if (message.response) {
			
			var current = this.getFirstFromQueue();
			
			if (!current)
				return;
			
			if (current.state == 1) {
				if (message.response == 'ErrorResponse') {
					console.error(message.response);
					if (current && current.callback)
						current.callback(message);
					current.state = 2;
				}
				else {
					if (current && current.callback)
						current.callback(null, message.payload);
					current.state = 2;
				}
			}
			
			this.iterateQueue();
		}
		else if (message.event) {
			this.emit(message.event, message.payload);
		}
	}

	closeHandler(evt) {
		
		this.authenticated = false;
		
		if (evt && evt.code > 1000)
			console.error(evt.code, evt.reason);
		if (evt && [1000,1002,1003,1007,4001].indexOf(evt.code) === -1)
			this.retry();
		
		this.emit('close');
	}

	errorHandler(evt) {
		this.emit('error');
		console.log(evt);
		this.retry();
	}

	retry() {
		if (this.retryTimer)
			clearTimeout(this.retryTimer);
		
		this.retryTimer = setTimeout(() => this.connect(), DEFAULT_TIMEOUT);
	}

	async auth() {
		const token = await tokenProvider.get();
		this.require('auth', {token: token}, (error) => {
			if (error) {
				console.error(error);
				this.emit('error', error);
				return;
			}
			this.authenticated = true;
			this.emit('auth', this);
		});
	}

	require() {
		
		if (this.ws.readyState !== 1) {
			const error = new Error("websocket is not ready yet");
			callback(error);
			return Promise.reject(error);
		}
		
		var args = this.constructor.getRequestArguments.apply(null, arguments);
		
		if (!args.request) {
			const error = new Error("request is required");
			callback(error);
			return Promise.reject(error);
		}
		
		if (args.request !== 'auth' && !this.authenticated) {
			const error = new Error("wsi connection is not authenticated");
			callback(error);
			return Promise.reject(error);
		}
		
		return new Promise((resolve, reject) => {
			this.push(args.request, args.params, (error, response) => {
				args.callback(error, response);
				if (error)
					reject(error);
				else
					resolve(response || undefined);
			});
			this.iterateQueue();
		});
	}

	on() {
		
		var args = this.constructor.getListenerArguments.apply(null, arguments);
		
		this.constructor.__proto__.prototype.on.call(this, args.event, args.handler);
		
		if (['auth', 'error', 'close', 'connect'].indexOf(args.event) > -1)
			return this;
		
		this.send({
			event: args.event,
			filter: args.filter || {}
		});
		
		return this;
	}

	once() {
		
		var args = this.constructor.getListenerArguments.apply(null, arguments);
		
		this.constructor.__proto__.prototype.once.call(this, args.event, args.handler);
		
		if (['auth', 'error', 'close', 'connect'].indexOf(args.event) > -1)
			return this;
		
		this.send({
			event: args.event,
			filter: args.filter || {},
			once: true
		});
		
		return this;
	}

	getState() {
		return this.ws.readyState;
	}

	isOpened() {
		return this.ws.readyState == 1 ? true : false;
	}

	isAuthenticated() {
		return this.authenticated;
	}
	
	static getRequestArguments() {
		
		var args = Array.prototype.slice.call(arguments);
		var request = null;
		var params = {};
		var callback = function(error, responseData){};
		
		for (var i in args) {
			if (args[i].constructor === String) request = args[i];
			else if (args[i].constructor === Object) params = args[i];
			else if (args[i].constructor === Function) callback = args[i];
		}
		
		return {
			request: request,
			params: params,
			callback: callback
		};
	}

	static getListenerArguments() {
		
		var args = Array.prototype.slice.call(arguments);
		var event = null;
		var filter = {};
		var handler = function(error, responseData){};
		
		for (var i in args) {
			if (args[i].constructor === String) event = args[i];
			else if (args[i].constructor === Object) filter = args[i];
			else if (args[i].constructor === Function) handler = args[i];
		}
		
		return {
			event: event,
			filter: filter,
			handler: handler
		};
	}

	static bufferToArray(buffer) {
		var view = new DataView(buffer);
		var array = [];
		for (var i=0; i<view.byteLength; ++i)
			array.push(view.getUint8(i));
		return array;
	}
}



// WEBPACK FOOTER //
// ./~/vwa/wsi/Connection.js